import styled from '@emotion/styled';
import { Checkbox } from '@mui/material';
import SearchField from '@shared/components/core/search/SearchField';
import TinyButton from '@shared/components/core/tiny/TinyButton';
import TinyHeader from '@shared/components/core/tiny/TinyHeader';
import TinyValues from '@shared/components/core/tiny/TinyValues';
import { McpToolSummary } from '@shared/models/mcp/McpServerModel';
import { serverToolTokenTotal } from '@shared/models/mcp/mcpTokenEstimate';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface IMcpToolSelectorProps {
    tools: McpToolSummary[];
    /** Bare names of the tools that are OFF. */
    disabledTools: string[];
    /** The assistant's context window in tokens, for the "% of context" badge. Omit to hide it. */
    contextWindow?: number;
    disabled?: boolean;
    /** Receives the complete next disabled set — the caller persists it. */
    onChange: (disabledTools: string[]) => void;
}

/**
 * The per-tool on/off list for one MCP server, with its token/context cost. Shared by the server editor
 * and the server detail dialog: turning a noisy tool off is the main thing a user does to a working
 * connector, so it can't be something only the edit form offers.
 *
 * Selection is reported as the whole next disabled set rather than a single toggle, so the caller has one
 * value to persist however it stores servers.
 */
export default function McpToolSelector({
    tools,
    disabledTools,
    contextWindow,
    disabled,
    onChange,
}: Readonly<IMcpToolSelectorProps>) {
    const { t } = useTranslation('common');
    const [query, setQuery] = useState('');

    const totalTokens = serverToolTokenTotal({ cachedTools: tools }, { onlyEnabled: false });
    const enabledTokens = serverToolTokenTotal({ cachedTools: tools, disabledTools }, { onlyEnabled: true });
    const enabledCount = tools.filter((tool) => !disabledTools.includes(tool.name)).length;
    const contextPct =
        contextWindow && contextWindow > 0 ? Math.round((enabledTokens / contextWindow) * 100) : undefined;

    // A server can advertise dozens of tools, so the list is filtered by name and description. The
    // counts above it stay whole-server totals — they describe the connector, not the current filter.
    const filtered = !!query.trim();
    const visibleTools = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) return tools;
        return tools.filter(
            (tool) => tool.name.toLowerCase().includes(trimmed) || !!tool.description?.toLowerCase().includes(trimmed)
        );
    }, [tools, query]);

    const toggleTool = (name: string, enabled: boolean) =>
        onChange(enabled ? disabledTools.filter((n) => n !== name) : [...new Set([...disabledTools, name])]);

    /**
     * Bulk enable/disable, reported as the whole next disabled set. With a search in place both act on
     * the rows the user can actually see — reaching past the filter to the tools it hides is the one
     * thing a bulk action here must never do, so those keep whatever state they already had.
     */
    const handleEnable = () => {
        if (filtered) {
            onChange(disabledTools.filter((n) => !visibleTools.some((tool) => tool.name === n)));
        } else {
            onChange([]);
        }
    };

    const handleDisable = () => {
        if (filtered) {
            onChange([...new Set([...disabledTools, ...visibleTools.map((tool) => tool.name)])]);
        } else {
            onChange(tools.map((tool) => tool.name));
        }
    };

    return (
        <>
            <TinyHeader size="small" title={t('mcp.tools.label')} description={t('mcp.tools.help')} />
            <ToolsControls>
                <SearchWrap>
                    <SearchField
                        label=""
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onClear={() => setQuery('')}
                        placeholder={t('mcp.tools.searchPlaceholder')}
                    />
                </SearchWrap>
                {/* Bulk selection is a shortcut for the checkboxes below, not an action in its own right —
                    underlined tiny text keeps it from reading as heavier than the list it edits. */}
                <ToolsActions>
                    <TinyButton
                        title={filtered ? t('mcp.tools.enableSelected') : t('mcp.tools.enableAll')}
                        variant="underline_diminished"
                        disabled={disabled}
                        onClick={handleEnable}
                    />
                    <TinyButton
                        title={filtered ? t('mcp.tools.disableSelected') : t('mcp.tools.disableAll')}
                        variant="underline_diminished"
                        disabled={disabled}
                        onClick={handleDisable}
                    />
                </ToolsActions>
            </ToolsControls>
            {/* Value-first, so the numbers line up down the row rather than trailing their units. */}
            <ToolsSummary>
                <TinyValues
                    variant="text"
                    value={`${enabledCount}/${tools.length}`}
                    title={t('mcp.tools.label')}
                    icon="listChecks"
                />
                <TinyValues
                    variant="text"
                    value={`~${enabledTokens.toLocaleString()}`}
                    title={t('mcp.tools.tokensLabel')}
                />
                {contextPct !== undefined && (
                    <TinyValues
                        value={`~${contextPct}%`}
                        title={t('mcp.tools.contextLabel')}
                        variant={contextPct >= 25 ? 'diminished_error' : 'diminished_accent'}
                    />
                )}
                {totalTokens !== enabledTokens && (
                    <TinyValues value={`~${totalTokens.toLocaleString()}`} title={t('mcp.tools.allToolsLabel')} />
                )}
            </ToolsSummary>
            <ToolList>
                {visibleTools.length === 0 && <EmptyRow>{t('noResults')}</EmptyRow>}
                {visibleTools.map((tool) => {
                    const enabled = !disabledTools.includes(tool.name);
                    return (
                        <ToolRow key={tool.name} $enabled={enabled}>
                            <Checkbox
                                size="small"
                                checked={enabled}
                                disabled={disabled}
                                onChange={(e) => toggleTool(tool.name, e.target.checked)}
                                slotProps={{ input: { 'aria-label': tool.name } }}
                            />
                            <ToolText>
                                <ToolName>{tool.name}</ToolName>
                                {tool.description && <ToolDescription>{tool.description}</ToolDescription>}
                            </ToolText>
                            {tool.tokenEstimate !== undefined && (
                                <ToolTokens>
                                    {t('mcp.tools.tokens', {
                                        tokens: tool.tokenEstimate.toLocaleString(),
                                    })}
                                </ToolTokens>
                            )}
                        </ToolRow>
                    );
                })}
            </ToolList>
        </>
    );
}

/** Search takes the room; the bulk actions sit against the right and wrap under it when space runs out. */
const ToolsControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const SearchWrap = styled.div`
    flex: 1 1 12rem;
    min-width: 0;
`;

const ToolsActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
`;

const ToolsSummary = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: ${(props) => props.theme.color.textBody};
`;

const EmptyRow = styled.div`
    padding: 0.5rem;
    font-size: 0.75rem;
    color: ${(props) => props.theme.color.textBody};
`;

const ToolList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${(props) => props.theme.color.border};
    border-radius: 0.5rem;
    overflow: hidden;
    max-height: 50vh;
    overflow-y: auto;
`;

const ToolRow = styled.div<{ $enabled?: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.35rem 0.5rem;
    opacity: ${(props) => (props.$enabled ? 1 : 0.55)};

    & + & {
        border-top: 1px solid ${(props) => props.theme.color.border};
    }
`;

const ToolText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    flex: 1;
    padding-top: 0.35rem;
`;

const ToolName = styled.span`
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${(props) => props.theme.color.text};
    word-break: break-word;
`;

const ToolDescription = styled.span`
    font-size: 0.6875rem;
    color: ${(props) => props.theme.color.textBody};
`;

const ToolTokens = styled.span`
    flex: none;
    align-self: center;
    font-size: 0.6875rem;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.theme.color.textBody};
    white-space: nowrap;
`;
